import { useSyncExternalStore } from 'react';
import { DEFAULT_DISPLAY_PREFS, type DisplayPrefs, type MeDto } from '@werft/contracts';
import { api } from './api.js';
import { storage } from './storage.js';

/**
 * 0.151 (ADR 0153): persönliche Darstellung – System/Hell/Dunkel, Dichte, Bewegung.
 * `public/theme-boot.js` wendet den lokalen Startwert schon vor dem ersten Zeichnen an; hier wird er geändert,
 * mit dem Konto abgeglichen (Konto gewinnt, sobald es einen Wert hat) und an <html> gespiegelt.
 * Ohne Anmeldung bleibt die Wahl gerätebezogen.
 */
const KEY = 'werft.display';
type Boot = { apply: (d: Partial<DisplayPrefs>) => void; current: Partial<DisplayPrefs> };
const boot = (): Boot | undefined => (window as unknown as { __werftDisplay?: Boot }).__werftDisplay;
const listeners = new Set<() => void>();
let version = 0;

function valid(d: unknown): DisplayPrefs {
  const x = (d && typeof d === 'object' ? d : {}) as Partial<DisplayPrefs>;
  return {
    theme: x.theme === 'light' || x.theme === 'dark' ? x.theme : 'system',
    density: x.density === 'compact' ? 'compact' : 'normal',
    motion: x.motion === 'reduced' ? 'reduced' : 'subtle',
  };
}

export function currentDisplay(): DisplayPrefs {
  return valid(boot()?.current ?? storage.get(KEY));
}

/** Auflösen und an <html> setzen (gleiche Regeln wie theme-boot.js) */
function paint(d: DisplayPrefs) {
  const b = boot();
  if (b) {
    b.current = d;
    b.apply(d);
    return;
  }
  const root = document.documentElement;
  const dark = d.theme === 'dark' || (d.theme === 'system' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  root.dataset.theme = dark ? 'dark' : 'light';
  root.dataset.themeChoice = d.theme;
  root.dataset.density = d.density;
  root.dataset.motion = d.motion;
  root.style.colorScheme = dark ? 'dark' : 'light';
}

/** Lokal anwenden (Vorschau oder gespeicherte Wahl) – ohne Konto */
export function applyDisplay(d: DisplayPrefs, persist = true) {
  if (persist) storage.set(KEY, d);
  paint(d);
  version++;
  listeners.forEach((l) => l());
}

/** Wählen: sofort sichtbar, dann im Konto (andere Geräte übernehmen es) */
export async function saveDisplay(d: DisplayPrefs, signedIn: boolean) {
  applyDisplay(d);
  if (signedIn) await api.put('/api/me/display', d);
}

/** Konto ↔ Gerät: hat das Konto einen Wert, gilt er; sonst wird eine schon getroffene Gerätewahl übernommen */
export function syncDisplay(me: MeDto | null | undefined) {
  if (!me) return;
  const local = storage.get<DisplayPrefs>(KEY);
  if (me.display) {
    const server = valid(me.display);
    if (JSON.stringify(server) !== JSON.stringify(currentDisplay())) applyDisplay(server);
  } else if (local && JSON.stringify(valid(local)) !== JSON.stringify(DEFAULT_DISPLAY_PREFS)) {
    void api.put('/api/me/display', valid(local)).catch(() => {});
  }
}

export const subscribeDisplay = (l: () => void) => (listeners.add(l), () => listeners.delete(l));

export function useDisplayPrefs(): DisplayPrefs {
  useSyncExternalStore(subscribeDisplay, () => version);
  return currentDisplay();
}

/** Tatsächlich aufgelöster Modus (folgt auch dem System im Modus „System“) */
export function useResolvedTheme(): 'light' | 'dark' {
  return useSyncExternalStore(
    (l) => {
      const mo = new MutationObserver(l);
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
      return () => mo.disconnect();
    },
    () => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'),
  );
}
